import {
  ChevronDoubleDownIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/20/solid'
import type { MenuItem as MItem } from '../navigation.presenter'
import * as HeroIcons from '@heroicons/react/16/solid'

type Props = {
  menuItem: MItem
  // Copy comes down as props: this is a dumb component (§9.6) with no Presenter
  // to resolve translations from.
  expandLabel: string
  collapseLabel: string
  // Indentation depth, relative to whichever list this item's root is rendered in —
  // NOT menuItem.level (that's the absolute tree depth, used by the presenter's
  // columnPath indexing). A column's own top-level items always start at depth 0,
  // regardless of how deep they are in the full tree, since the column itself
  // already conveys the hierarchy. Only inline-expanded children increment it.
  depth?: number
}

const getIconComponent = (iconName: string, className: string) => {
  const Icon = HeroIcons[iconName as keyof typeof HeroIcons]
  return Icon ? <Icon className={className} /> : null
}

// Tailwind UI's vertical-nav palette, routed through the shell-* theme tokens
// (src/index.css) instead of literal colors — swapping tokens (e.g. for dark
// mode) re-themes every nav row without touching this file. Each nesting depth
// gets a progressively darker resting/hover/current shade, so deeper items
// read as "further in" beyond just the indentation.
const DEPTH_BG = ['', 'bg-shell-depth-1', 'bg-shell-depth-2', 'bg-shell-depth-3']
const DEPTH_BG_HOVER = [
  'hover:bg-shell-depth-1',
  'hover:bg-shell-depth-2',
  'hover:bg-shell-depth-3',
  'hover:bg-shell-depth-4',
]
const DEPTH_BG_CURRENT = ['bg-shell-depth-1', 'bg-shell-depth-2', 'bg-shell-depth-3', 'bg-shell-depth-4']

const ICON_CURRENT = 'text-shell-accent'
const ICON_DEFAULT = 'text-shell-ink-muted group-hover:text-shell-accent'

const rowBackground = (depth: number, current: boolean) => {
  const d = Math.min(depth, DEPTH_BG.length - 1)
  return current
    ? `${DEPTH_BG_CURRENT[d]} text-shell-accent`
    : `${DEPTH_BG[d]} ${DEPTH_BG_HOVER[d]} text-shell-ink-secondary hover:text-shell-accent`
}

// focus-visible:relative + z-10: rows sit right up against their siblings (no
// gap in the split-expand case — see the -ml-2 note below), so a focused
// row's outline needs to paint above the adjacent row/button or it gets
// clipped by whichever one comes later in DOM/paint order.
// scroll-my-1: keyboard focus on the first/last row scrolls it flush against
// the ul's own edge, clipping the outline's outset (2px width + 2px offset)
// against the scroll container — this reserves that 4px so it scrolls into view too.
const ROW =
  'group flex flex-1 items-center gap-x-2 rounded-md py-2 text-sm/6 font-semibold focus-visible:relative focus-visible:z-10 scroll-my-1'

export const NavItemComponent = ({ menuItem, expandLabel, collapseLabel, depth = 0 }: Props) => {
  const interactive = menuItem.clickable || menuItem.expandable
  const splitExpand = menuItem.expandable && menuItem.clickable
  const clampedDepth = Math.min(depth, DEPTH_BG.length - 1)
  const depthBg = DEPTH_BG[clampedDepth]
  const depthBgHover = DEPTH_BG_HOVER[clampedDepth]

  const label = (
    <>
      {menuItem.preIcon &&
        getIconComponent(menuItem.preIcon, `size-4 shrink-0 ${menuItem.current ? ICON_CURRENT : ICON_DEFAULT}`)}
      <span className="truncate">{menuItem.buttonText}</span>
      {menuItem.showNewsCount && (
        <span
          aria-hidden="true"
          className="ml-auto w-9 min-w-max rounded-full bg-shell-surface px-2.5 py-0.5 text-center text-xs/5 font-medium whitespace-nowrap text-shell-ink-secondary outline-1 -outline-offset-1 outline-shell-border"
        >
          {menuItem.newsCount}
        </span>
      )}
      {/* External links (urlTo, no in-app routesTo) — "opens elsewhere" cue, same
          trailing slot as the badge (mutually exclusive, a link doesn't have a count). */}
      {menuItem.urlTo && (
        <ArrowTopRightOnSquareIcon
          aria-hidden="true"
          className={`ml-auto size-4 shrink-0 ${menuItem.current ? ICON_CURRENT : ICON_DEFAULT}`}
        />
      )}
    </>
  )

  // columnView nodes open their children in a new sidebar column (rendered by
  // NavigationComponent from viewModel.columns) instead of expanding inline here —
  // same toggle/open concept as `expand`/`expanded`, chevron just doesn't rotate
  // (static "opens sideways" cue vs. the rotating "opens below" cue for inline expand).
  const toggleOpen = menuItem.columnView ? menuItem.openColumn! : menuItem.expand
  const isOpen = menuItem.columnView ? menuItem.columnOpen : menuItem.expanded

  // Inline expand: down = "expand", rotates to up = "collapse" (content unfolds
  // below). columnView: right = "open the column", rotates to left = "close it"
  // (content opens sideways) — different base icon, same rotate-180-on-open rule.
  /**
   * `doubled` is the split-expand case: that chevron is a button of its own,
   * sitting on the same background as the label button beside it, so the icon
   * is the only thing distinguishing the two targets. A single chevron there
   * looks like every other row's inline one — which is exactly the thing it is
   * not.
   */
  const renderChevron = (extraClassName: string = '', doubled = false) => {
    const Icon = menuItem.columnView ? ChevronRightIcon : doubled ? ChevronDoubleDownIcon : ChevronDownIcon
    return (
      <Icon
        className={`size-4 shrink-0 transition-transform ${menuItem.current ? ICON_CURRENT : ICON_DEFAULT}
                    ${isOpen ? 'rotate-180' : ''} ${extraClassName}`}
        aria-hidden="true"
      />
    )
  }

  return (
    <>
      {/* Group heading — a label and a rule, carried by the group's first visible
          member (presenter: MenuItem.groupStart). Rendered here rather than in
          NavigationComponent so grouping works in every list that shows nav
          items: the primary list, an open column, and inline-expanded children.
          pt-4 is the gap above; the gap below comes from the spacer the last
          member renders, so an empty group contributes neither. */}
      {menuItem.groupStart && (
        <div className="flex items-center gap-x-2 px-2 pt-4 pb-1 select-none" style={{ paddingLeft: depth * 16 + 8 }}>
          <span className="truncate text-xs/5 font-semibold tracking-wide text-shell-ink-muted uppercase">
            {menuItem.groupStart}
          </span>
          <span className="h-px flex-1 bg-shell-border" aria-hidden="true" />
        </div>
      )}
      {/* items-stretch (the flex default) so the chevron button always matches the
          label button's height instead of shrinking to fit its own smaller icon-only
          content — each button's own p-2 then only controls horizontal spacing. */}
      <div className="flex" style={{ paddingLeft: depth * 16 }}>
        {interactive ? (
          <button
            type="button"
            onClick={() => (menuItem.clickable ? menuItem.navigate() : toggleOpen())}
            className={`${ROW} px-2 ${rowBackground(depth, menuItem.current)}`}
          >
            {label}
            {/* Docked to the far right regardless of whether a badge already claims
                ml-auto — when there's no badge, nothing else pushes the chevron over. */}
            {menuItem.expandable && !menuItem.clickable && renderChevron(menuItem.showNewsCount ? '' : 'ml-auto')}
          </button>
        ) : (
          <div
            className={`flex flex-1 items-center gap-x-2 rounded-md p-2 text-sm/6 font-semibold text-shell-ink-muted ${depthBg}`}
          >
            {label}
          </div>
        )}

        {/* Both clickable+expandable: dedicated chevron button on the right (same side
            as every other row's expand arrow), separate from the label button so
            clicking the label always navigates, never also expands. Same depth
            background as the label button so the two halves read as one row;
            the doubled chevron is what marks it as a separate target.
            Full p-2 on all four sides here (matches the label button's own padding
            exactly) — the -ml-2 slides the button left by one padding-width so it
            overlaps the label button's own trailing padding instead of stacking a
            second 8px gap after it. Net result: badge-to-chevron gap and the row's
            true right edge both land exactly where the inline (single-button) case
            puts them, with neither button's padding ever being asymmetric. */}
        {splitExpand && (
          <button
            type="button"
            onClick={() => toggleOpen()}
            aria-label={isOpen ? collapseLabel : expandLabel}
            className={`group flex items-center justify-center rounded-md p-2 -ml-2 focus-visible:relative focus-visible:z-10 scroll-my-1 ${depthBg} ${depthBgHover}`}
          >
            {renderChevron('', true)}
          </button>
        )}
      </div>
      {!menuItem.columnView &&
        menuItem.expanded &&
        menuItem.children?.map((child, i) => (
          <NavItemComponent
            key={child.key + i}
            menuItem={child}
            expandLabel={expandLabel}
            collapseLabel={collapseLabel}
            depth={depth + 1}
          />
        ))}
      {/* Closes the group off. A spacer after the children rather than a margin
          on the row: the last member may itself be expanded, and the gap belongs
          below its children, not between it and them. */}
      {menuItem.groupEnd && <div className="h-4" aria-hidden="true" />}
    </>
  )
}
